import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  listModels,
  addModel,
  deleteModel,
  type Model,
  type AddModelData,
} from '../../api/models';
import { DataTable, type Column } from '../common/DataTable';
import { Modal } from '../common/Modal';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { StatusBadge } from '../common/StatusBadge';
import { ModelForm } from './ModelForm';

/**
 * Extract a human-friendly provider name from the litellm_params.model string.
 * E.g. "bedrock/us.anthropic.claude-sonnet-4-20250514-v1:0" -> "bedrock"
 */
function extractProvider(model: Model): string {
  const litellmModel = model.litellm_params?.model ?? '';
  const slashIdx = litellmModel.indexOf('/');
  if (slashIdx > 0) {
    return litellmModel.substring(0, slashIdx);
  }
  return litellmModel ? 'custom' : 'unknown';
}

/**
 * Extract the account identifier from litellm_params.aws_role_name.
 * Returns a short label like "123456789012" or "Local".
 */
function extractAccount(model: Model): string {
  const roleArn = model.litellm_params?.aws_role_name as string | undefined;
  if (!roleArn) return 'Local';
  const match = roleArn.match(/arn:aws:iam::(\d{12}):role\//);
  return match ? match[1] : 'Local';
}

/**
 * Extract the model ID (unique identifier) from the model_info.
 */
function getModelId(model: Model): string {
  return (model.model_info?.id as string) ?? '';
}

/**
 * Format a date string for display, or return a dash if unavailable.
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
}

export function ModelsView() {
  const { getJwtToken } = useAuth();

  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Model | null>(null);

  const fetchModels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await listModels(token);
      setModels(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load models');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const handleAdd = async (data: AddModelData) => {
    const token = await getJwtToken();
    if (!token) return;
    await addModel(data, token);
    setShowAddModal(false);
    setSuccess(`Model "${data.modelName}" added successfully.`);
    await fetchModels();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const modelId = getModelId(deleteTarget);
    if (!modelId) {
      setError('Cannot delete: model has no ID.');
      setDeleteTarget(null);
      return;
    }
    try {
      const token = await getJwtToken();
      if (!token) return;
      await deleteModel(modelId, token);
      setDeleteTarget(null);
      setSelectedModel(null);
      setSuccess(`Model "${deleteTarget.model_name}" deleted.`);
      await fetchModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete model');
      setDeleteTarget(null);
    }
  };

  const handleRowClick = (model: Model) => {
    setSelectedModel(model);
  };

  const columns: Column<Model>[] = [
    {
      key: 'model_name',
      header: 'Model Name',
      render: (m) => <strong>{m.model_name}</strong>,
    },
    {
      key: 'provider_model',
      header: 'Provider / Model',
      render: (m) => (
        <code className="models-view__model-id">
          {m.litellm_params?.model ?? '--'}
        </code>
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (m) => (
        <span className="models-view__provider">{extractProvider(m)}</span>
      ),
    },
    {
      key: 'account',
      header: 'Account',
      render: (m) => <code>{extractAccount(m)}</code>,
    },
    {
      key: 'status',
      header: 'Status',
      render: () => <StatusBadge status="active" />,
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (m) => formatDate(m.model_info?.created_at as string),
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      render: (m) => (
        <button
          className="settings-card__delete"
          onClick={(ev) => {
            ev.stopPropagation();
            setDeleteTarget(m);
          }}
          type="button"
          title="Delete model"
        >
          x
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Models</h1>
        <div className="view__actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowAddModal(true)}
            type="button"
          >
            + Add Model
          </button>
          <button
            className="btn btn--secondary"
            onClick={fetchModels}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert--success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} type="button">
            Dismiss
          </button>
        </div>
      )}

      {loading && models.length === 0 ? (
        <div className="view__empty">Loading models...</div>
      ) : (
        <DataTable<Model>
          columns={columns}
          data={models}
          onRowClick={handleRowClick}
          keyExtractor={(m) => getModelId(m) || m.model_name}
          emptyMessage="No models found. Add a model to get started."
        />
      )}

      {/* Add Model Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Model"
        wide
      >
        <ModelForm
          onSubmit={handleAdd}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      {/* Model Detail Panel */}
      <Modal
        isOpen={!!selectedModel}
        onClose={() => setSelectedModel(null)}
        title={selectedModel ? `Model: ${selectedModel.model_name}` : 'Model Details'}
        wide
      >
        {selectedModel && (
          <div className="models-view__detail">
            <section className="models-view__detail-section">
              <h3 className="models-view__detail-heading">General</h3>
              <div className="models-view__detail-row">
                <span className="models-view__detail-label">Model Name:</span>
                <span>{selectedModel.model_name}</span>
              </div>
              <div className="models-view__detail-row">
                <span className="models-view__detail-label">Provider Model:</span>
                <code>{selectedModel.litellm_params?.model ?? '--'}</code>
              </div>
              <div className="models-view__detail-row">
                <span className="models-view__detail-label">Provider:</span>
                <span>{extractProvider(selectedModel)}</span>
              </div>
              <div className="models-view__detail-row">
                <span className="models-view__detail-label">Model ID:</span>
                <code>{getModelId(selectedModel) || '--'}</code>
              </div>
            </section>

            <section className="models-view__detail-section">
              <h3 className="models-view__detail-heading">LiteLLM Params</h3>
              <pre className="models-view__detail-json">
                {JSON.stringify(selectedModel.litellm_params, null, 2)}
              </pre>
            </section>

            <section className="models-view__detail-section">
              <h3 className="models-view__detail-heading">Model Info</h3>
              <pre className="models-view__detail-json">
                {JSON.stringify(selectedModel.model_info, null, 2)}
              </pre>
            </section>

            <div className="modal__actions">
              <button
                type="button"
                className="modal__cancel"
                onClick={() => setSelectedModel(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="modal__submit modal__submit--danger"
                onClick={() => {
                  setDeleteTarget(selectedModel);
                }}
              >
                Delete Model
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Model"
        message={`Are you sure you want to delete model "${deleteTarget?.model_name}"? This will remove it from the LiteLLM proxy configuration.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
